import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { useSnackbar } from 'contexts/SnackbarProvider';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from 'services/ApiService';
import DynamicTradeService from 'services/DynamicTradeService';
import Loader from '../../parts/Loader';
import ConfirmationDialog from 'pages/shared/ConfirmationDialog';
import { useDialogService } from 'contexts/DialogProvider';

const ListDynamicTrade = () => {
    const [dynamicTradeParameters, setDynamicTradeParameters] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { showError, showMessage } = useSnackbar();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate();
    const [jobRunning, setJobRunning] = useState(false);
    const { open } = useDialogService();


    useEffect(() => {
        getAllDynamicTrades();
        getJobStatus();
    }, []);

    const getAllDynamicTrades = async () => {
        try {
            setIsLoading(true);
            const response = await DynamicTradeService.getAllDynamicTrades();
            const dynamicTradeParameters = response?.data ?? [];
            setDynamicTradeParameters(dynamicTradeParameters);
        } catch (error) {
            console.error("Error fetching dynamic trade parameters:", error);
            setDynamicTradeParameters([]);
        } finally {
            setIsLoading(false);
        }
    };

    const getJobStatus = async () => {
        try {
            setIsLoading(true);
            const response = await DynamicTradeService.getJobStatus();
            const jobStatus = response?.data?.status;
            if (jobStatus === 'RUNNING') {
                setJobRunning(true);
            } else {
                setJobRunning(false);
            }
        } catch (error) {
            console.error("Error fetching job status:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const startJob = async () => {
        setIsLoading(true);
        try {
            const response = await DynamicTradeService.startJob();
            setJobRunning(true);
            showMessage("Job started successfully.");
        } catch (error) {
            showError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const stopJob = async () => {
        setIsLoading(true);
        try {
            const response = await DynamicTradeService.stopJob();
            setJobRunning(false);
            showMessage("Job stopped successfully.");
        } catch (error) {
            showError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteDynamicTradeDialog = async (trade) => {
        const result = await open(ConfirmationDialog, {
            header: "Delete Dynamic Trade",
            data: {
                message: `Please confirm to delete the dynamic trade - ${trade.id}.`,
            },
            width: "40%"
        });

        if (result === 'CONFIRM') {
            deleteDynamicTrade(trade);
        }
    };

    const deleteDynamicTrade = async (trade) => {
        try {
            setIsLoading(true);
            const response = await DynamicTradeService.deleteDynamicTrade(trade);
            showMessage("Dynamic trade deleted successfully.");
            getAllDynamicTrades();
        } catch (error) {
            showError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <>
            {isLoading && <Loader />}
            <div className="table-wrapper">
                <div className="table-heading card-line">
                    <div className="heading-name">Dynamic Trade Manager
                        <span style={{ fontSize: '12px', color: '#888', marginLeft: '1rem' }}>{jobRunning ? "Job is running" : "Job is stopped"}</span>
                    </div>
                    <div>
                        <Button color="primary" variant="contained" disableElevation
                            onClick={() => navigate('/dynamicTradeManager/add')}>
                            Create Dynamic Trade
                        </Button>
                        <Button style={{ marginLeft: '8px' }} color="primary" variant="contained"
                            disableElevation onClick={() => startJob()}>
                            Trigger Job
                        </Button>
                        <Button style={{ marginLeft: '8px' }} color="primary" variant="contained"
                            disableElevation onClick={() => stopJob()}>
                            Stop Job
                        </Button>
                    </div>
                </div>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Trade Name</TableCell>
                            <TableCell>Client</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            dynamicTradeParameters.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" className="empty-message">
                                        <img src="images/empty.svg" alt="empty-message" />
                                        <p className="crypt-grayscale-600 pb-3">No records</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                dynamicTradeParameters
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((trade) => (
                                        <TableRow key={trade.id}>
                                            <TableCell scope="row">
                                                {trade.tradeName}
                                            </TableCell>
                                            <TableCell>{trade.clientName}</TableCell>
                                            <TableCell>{trade.status}</TableCell>
                                            <TableCell>
                                                <div>
                                                    <Button style={{ marginRight: '10px' }} color="primary" variant="contained"
                                                        disableElevation
                                                        onClick={() => navigate(`/dynamicTradeManager/edit/${trade.id}`)}>
                                                        Edit
                                                    </Button>
                                                    <Button color="primary" variant="contained"
                                                        disableElevation onClick={() => openDeleteDynamicTradeDialog(trade)}>
                                                        Delete
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={dynamicTradeParameters.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </div>
        </>
    )
}

export default ListDynamicTrade;